import React, { useState } from 'react';
import { Button, ButtonV2, Input } from '@asigna/kit';
import axios from 'axios';
import { ModalCloseButton } from 'components/ModalCloseButton';
import { NotificationCard } from 'components/NotificationCard';
import { toast } from 'sonner';

interface AddCustomAppDialogProps {
  onClose: () => void;
}

function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);

    return true;
  } catch (e) {
    return false;
  }
}

interface AppsMetadata {
  title?: string;
  description?: string;
  favicon?: string;
  url?: string;
}

export const AddCustomAppDialog: React.FC<AddCustomAppDialogProps> = ({ onClose }) => {
  const [appUrl, setAppUrl] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchMetadata = async (url: string) => {
    try {
      const response = await axios.get(url, { responseType: 'text' });
      const html = response.data;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const title = doc.querySelector('title')?.innerText || 'No title';

      const description =
        doc.querySelector('meta[name="description"]')?.getAttribute('content') || 'No description';

      let favicon =
        doc.querySelector('link[rel="icon"]')?.getAttribute('href') ||
        doc.querySelector('link[rel="shortcut icon"]')?.getAttribute('href') ||
        '/favicon.ico';

      if (favicon && !favicon.startsWith('http')) {
        favicon = new URL(favicon, url).href;
      }

      setError('');

      return { title, description, favicon };
    } catch (err) {
      setError('Error fetching Metadata from url');
    }
  };
  const handleAdd = async () => {
    try {
      setIsLoading(true);
      await fetchMetadata(appUrl).then((res) => {
        const savedApps = localStorage.getItem('customApps');
        const metadataArray: AppsMetadata[] = savedApps ? JSON.parse(savedApps) : [];
        metadataArray.push({
          url: appUrl,
          description: res?.description || '',
          favicon: res?.favicon || '',
          title: res?.title || '',
        });
        localStorage.setItem('customApps', JSON.stringify(metadataArray));
        toast(
          () => (
            <NotificationCard
              title="Custom application added"
              message="Application added to the Vault successfully."
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );
      });
      onClose();
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-16">
      <div className="flex items-start justify-between mb-16">
        <div className="flex flex-col gap-8">
          <p className="text-r20">Add Custom App</p>
        </div>
        <ModalCloseButton onClick={onClose} />
      </div>
      <div className="flex flex-col gap-8">
        <p className="text-r14">Enter the URL of the custom app you wish to add</p>
        <Input
          onChange={(e) => {
            setAppUrl((e.target as HTMLInputElement).value);
          }}
          placeholder="https://app.example"
          value={appUrl}
        />
        {!isValidUrl(appUrl) && appUrl.length > 0 && (
          <span className="text-r12 text-error">Wrong url</span>
        )}
      </div>
      <div className="flex gap-8 justify-center">
        <span className="text-r14 text-regular">Learn more about building</span>
        <button
          className="text-r14 text-primary"
          onClick={() =>
            window.open(
              'https://asigna.gitbook.io/asigna/developers/integrate-asigna-multisig-into-your-app/bitcoin'
            )
          }
        >
          Custom Apps
        </button>
      </div>
      {error.length > 0 && <span className="text-r12 text-error">{error}</span>}
      <div className="flex gap-16 mt-16">
        <ButtonV2 color="secondary" onClick={onClose} className="w-full">
          Cancel
        </ButtonV2>
        <Button
          colorType="primary"
          onClick={() => handleAdd()}
          disabled={appUrl.length === 0 || !isValidUrl(appUrl)}
          message="Add"
          fullWidth
          leftIcon={isLoading ? '/loading-gif.gif' : undefined}
        />
      </div>
    </div>
  );
};
